import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Loader2,
  ShieldCheck,
  MapPin,
  LogOut,
  Pencil,
  Check,
  ChevronRight,
} from "lucide-react";
import {
  getSessionProfileId,
  setSessionProfileId,
} from "../context/OnboardingContext";
import {
  profileSchema,
  sectionCompletion,
  totalCompletion,
  isFieldFilled,
} from "../data/profileSchema";
import SectionEditor from "../components/profile/SectionEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingSection, setEditingSection] = useState(null);

  useEffect(() => {
    const id = getSessionProfileId();
    if (!id) {
      navigate("/login");
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/profiles/${id}`);
        setProfile(res.data);
      } catch (e) {
        setError("Profil konnte nicht geladen werden.");
        if (e?.response?.status === 404) {
          setSessionProfileId(null);
          navigate("/login");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const logout = () => {
    setSessionProfileId(null);
    navigate("/");
  };

  const handleSaveSection = async (values) => {
    const res = await axios.patch(`${API}/profiles/${profile.id}`, values);
    setProfile(res.data);
    setEditingSection(null);
  };

  const total = useMemo(() => totalCompletion(profile), [profile]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-qh-slate">
        <Loader2 className="w-6 h-6 animate-spin mr-3" /> Profil wird geladen…
      </div>
    );
  }
  if (error || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center text-qh-slate">
        {error || "Kein Profil."}
      </div>
    );
  }

  const age = (() => {
    if (!profile.birth_date) return null;
    const [y, m, d] = profile.birth_date.split("-").map(Number);
    const b = new Date(y, (m || 1) - 1, d || 1);
    const now = new Date();
    let a = now.getFullYear() - b.getFullYear();
    const md = now.getMonth() - b.getMonth();
    if (md < 0 || (md === 0 && now.getDate() < b.getDate())) a--;
    return a;
  })();

  const verified = profile.verification_status === "verified";
  const mainPhoto = profile.photos?.[0];
  const photoUrl = (p) =>
    p?.url ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : null;

  return (
    <div className="min-h-screen bg-[#FFFBFB] font-body text-qh-ink pb-20">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-qh-line">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center shrink-0" data-testid="profile-logo">
            <img
              src="/assets/qhearts-logo.png"
              alt="QHearts"
              className="h-14 sm:h-16 w-auto object-contain select-none"
              draggable="false"
            />
          </Link>
          <button
            onClick={logout}
            className="text-sm text-qh-slate hover:text-qh-ink inline-flex items-center gap-1.5"
            data-testid="profile-logout"
          >
            <LogOut className="w-4 h-4" /> Abmelden
          </button>
        </div>
      </header>

      <motion.main
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto px-5 sm:px-8 pt-8 sm:pt-12"
        data-testid="profile-page"
      >
        {/* HEADER CARD */}
        <section className="rounded-3xl bg-white border border-qh-line overflow-hidden" data-testid="profile-header">
          <div className="grid sm:grid-cols-[280px_1fr]">
            <div className="relative aspect-[5/4] sm:aspect-auto bg-[#FFE4E6]">
              {mainPhoto ? (
                <img
                  src={photoUrl(mainPhoto)}
                  alt={profile.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-qh-slate text-sm">
                  Kein Bild
                </div>
              )}
              {verified && (
                <div className="absolute top-4 right-4 flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 border border-qh-line shadow-sm">
                  <ShieldCheck className="w-3.5 h-3.5 text-qh-rose" strokeWidth={2.5} />
                  <span className="text-[11px] font-semibold tracking-wide text-qh-ink">
                    VERIFIZIERT
                  </span>
                </div>
              )}
            </div>
            <div className="p-6 sm:p-8 flex flex-col">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="font-display text-3xl sm:text-4xl font-medium leading-tight">
                    {profile.name}
                    {age && <span className="text-qh-slate font-normal">, {age}</span>}
                  </h1>
                  {profile.headline && (
                    <p className="mt-2 font-display italic text-qh-rose text-lg">
                      {profile.headline}
                    </p>
                  )}
                  {profile.city && (
                    <p className="mt-2 text-sm text-qh-slate inline-flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" /> {profile.city}
                    </p>
                  )}
                </div>
              </div>

              {/* Progress */}
              <div className="mt-auto pt-6">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-sm font-medium text-qh-ink">
                    Profil-Vollständigkeit
                  </span>
                  <span
                    className="font-display text-3xl font-medium text-qh-rose"
                    data-testid="profile-percent"
                  >
                    {total.percent}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-qh-line overflow-hidden">
                  <div
                    className="h-full bg-qh-rose transition-all duration-700"
                    style={{ width: `${total.percent}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-qh-slate">
                  {total.filled} von {total.total} Feldern ausgefüllt.{" "}
                  {total.percent < 100
                    ? "Vervollständige dein Profil für bessere Matches."
                    : "Dein Profil ist komplett – stark!"}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTIONS */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="profile-sections">
          {profileSchema.map((section) => {
            const stat = sectionCompletion(section, profile);
            const complete = stat.percent === 100;
            const empty = stat.filled === 0;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => setEditingSection(section)}
                className="text-left rounded-3xl bg-white border border-qh-line p-6 hover:-translate-y-0.5 hover:shadow-[0_24px_48px_-24px_rgba(225,29,72,0.18)] transition-all flex flex-col"
                data-testid={`section-card-${section.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-xl font-medium text-qh-ink leading-tight">
                      {section.title}
                    </h3>
                    {section.description && (
                      <p className="mt-1 text-sm text-qh-slate">
                        {section.description}
                      </p>
                    )}
                  </div>
                  <span
                    className={`shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full ${
                      complete
                        ? "bg-qh-rose text-white"
                        : empty
                        ? "border border-qh-line text-qh-slate"
                        : "bg-[#FFE4E6] text-qh-rose"
                    }`}
                    aria-hidden="true"
                  >
                    {complete ? (
                      <Check className="w-4 h-4" strokeWidth={2.5} />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </span>
                </div>

                <div className="mt-5 flex items-center gap-3">
                  <div className="flex-1 h-1.5 rounded-full bg-qh-line overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${
                        complete ? "bg-qh-rose" : "bg-qh-rose/70"
                      }`}
                      style={{ width: `${stat.percent}%` }}
                    />
                  </div>
                  <span className="text-xs font-semibold text-qh-slate w-12 text-right">
                    {stat.filled}/{stat.total}
                  </span>
                </div>

                {/* Preview chips */}
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {section.fields
                    .filter((f) => isFieldFilled(profile?.[f.key], f))
                    .slice(0, 3)
                    .map((f) => (
                      <span
                        key={f.key}
                        className="text-[11px] px-2.5 py-1 rounded-full bg-[#FFFBFB] border border-qh-line text-qh-slate"
                      >
                        {f.label}
                      </span>
                    ))}
                  {empty && (
                    <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#FFFBFB] border border-qh-line text-qh-slate">
                      Noch nichts ausgefüllt
                    </span>
                  )}
                </div>

                <div className="mt-5 inline-flex items-center gap-1.5 text-sm text-qh-rose font-medium">
                  <Pencil className="w-3.5 h-3.5" />{" "}
                  {empty ? "Jetzt ausfüllen" : "Bearbeiten"}
                </div>
              </button>
            );
          })}
        </div>

        {/* MORE PHOTOS */}
        {profile.photos && profile.photos.length > 1 && (
          <section className="mt-10">
            <h2 className="font-display text-xl font-medium mb-3">Mehr von mir</h2>
            <div className="grid grid-cols-3 gap-3">
              {profile.photos.slice(1).map((p) => (
                <div
                  key={p.id}
                  className="aspect-square rounded-2xl overflow-hidden border border-qh-line bg-white"
                >
                  <img
                    src={photoUrl(p)}
                    alt="Profilbild"
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </section>
        )}
      </motion.main>

      {editingSection && (
        <SectionEditor
          section={editingSection}
          profile={profile}
          onClose={() => setEditingSection(null)}
          onSave={handleSaveSection}
        />
      )}
    </div>
  );
}
